import { Typography, Tag } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { PhoneOutlined, MailOutlined, ApiOutlined, ArrowDownOutlined, ArrowUpOutlined, PaperClipOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Snapshot } from 'valtio';
import { CommunicationRecord, CommunicationTagKey, LabelId, LabelSet, SignifyOrderStatusCheck } from '#types';
import { communicationTags } from './communicationTags';
import { apiOutcomeTags } from './apiOutcomeTags';
import FlexBox from '@/components/atoms/FlexBox';
import { LabelTags } from '@/components/atoms/LabelTags';
import { AttachmentFileTypeIcon } from '@/components/rendering/attachments/AttachmentFileTypeIcon';
import RenderActions from './RenderActions';
import { EmailFormData } from './EmailPopoverForm';
import { PhoneCallFormData } from './PhoneCallPopoverForm';

const { Text } = Typography;

type CommunicationLogDisplayMode = 'default' | 'compact';

interface CommunicationColumnsOptions {
    disabled?: boolean;
    /** The only state that drops the actions column: a printed page has no
     *  buttons. `disabled` greys the controls instead of removing them. */
    pdfExportMode?: boolean;
    fixedActions?: boolean;
    logdisplay?: CommunicationLogDisplayMode;
    onDelete?: (record: CommunicationRecord, deleteAttachments: boolean) => void;
    onEmailSave?: (record: CommunicationRecord, data: EmailFormData) => boolean | Promise<boolean>;
    onPhoneSave?: (record: CommunicationRecord, data: PhoneCallFormData) => boolean | Promise<boolean>;
    onLabelsSave?: (record: CommunicationRecord, labelIds: LabelId[]) => boolean | Promise<boolean>;
    onCheckOrderStatus?: (record: CommunicationRecord) => Promise<SignifyOrderStatusCheck | undefined>;
    labelSet?: Snapshot<LabelSet>;
}

const renderTag = (tagKey: CommunicationTagKey) => {
    const tagStyle = communicationTags[tagKey] || { color: 'default', label: tagKey };
    const isManual = tagKey === 'manual';

    return (
        <Tag
            color={tagStyle.color === 'default' ? undefined : tagStyle.color}
            variant={isManual ? 'filled' : 'outlined'}
            style={{ margin: 0 }}
        >
            {tagStyle.label}
        </Tag>
    );
};

export const communicationColumns = (options?: CommunicationColumnsOptions): ColumnType<CommunicationRecord>[] => {
    const { disabled = false, pdfExportMode = false, fixedActions = false, logdisplay = 'default', onDelete, onEmailSave, onPhoneSave, onLabelsSave, onCheckOrderStatus, labelSet } = options || {};
    const isCompact = logdisplay === 'compact';

    const baseColumns: ColumnType<CommunicationRecord>[] = [
        {
            dataIndex: 'typeTag',
            title: 'Type',
            width: 100,
            render: (_: any, record: CommunicationRecord) => {
                const icon = record.type === 'phone'
                    ? <PhoneOutlined />
                    : record.type === 'api'
                        ? <ApiOutlined />
                        : record.type === 'attachment'
                            ? <PaperClipOutlined />
                            : <MailOutlined />;
                const label = record.type === 'phone'
                    ? 'Call'
                    : record.type === 'api'
                        ? 'API'
                        : record.type === 'attachment'
                            ? 'Rename'
                            : 'Email';

                return (
                    <FlexBox noGrow justifyStart>
                        {icon} {label}
                    </FlexBox>
                );
            },
        },
    ];

    if (!isCompact) {
        baseColumns.push({
            dataIndex: 'timestamp',
            title: 'Date',
            width: 180,
            responsive: ['lg'],
            render: (_: any, record: CommunicationRecord) => {
                if (!record.timestamp) return '—';
                return dayjs(record.timestamp.toDate()).format('YYYY-MM-DD HH:mm');
            },
        });
    }

    baseColumns.push({
        dataIndex: 'details',
        title: 'Details',
        responsive: ['lg'],
        render: (_: any, record: CommunicationRecord) => {
                const timestamp = isCompact && record.timestamp
                    ? dayjs(record.timestamp.toDate()).format('YYYY-MM-DD HH:mm')
                    : null;
                const labels = labelSet
                    ? <LabelTags set={labelSet} labelIds={record.labelIds} />
                    : null;

                if (record.type === 'phone') {
                    const directionIcon = record.direction === 'inbound'
                        ? <ArrowDownOutlined style={{ color: '#52c41a' }} />
                        : record.direction === 'outbound'
                        ? <ArrowUpOutlined style={{ color: '#1890ff' }} />
                        : null;

                    const phoneNumber = record.phoneNumber?.formatted
                        || (record.phoneNumber?.components?.countryCode && record.phoneNumber?.components?.number
                            ? `${record.phoneNumber.components.countryCode} ${record.phoneNumber.components.number}`
                            : '');

                    const notes = record.notes || '';

                    return (
                        <FlexBox justifyStart wrap gap={0}>
                            {directionIcon}
                            {phoneNumber && (
                                <Text strong style={{ marginLeft: 5 }}>
                                    {phoneNumber}&nbsp;
                                </Text>
                            )}
                            {notes && (
                                <Text type="secondary" ellipsis style={{ maxWidth: '100%' }}>
                                    {notes}&nbsp;
                                </Text>
                            )}
                            {renderTag(record.tag)}
                            {labels}
                            {timestamp && (
                                <Text type="secondary">
                                    {timestamp}
                                </Text>
                            )}
                        </FlexBox>
                    );
                }

                if (record.type === 'api') {
                    const outcomeTag = apiOutcomeTags[record.outcome];
                    const summary = record.salesdocument
                        || record.quoteNumber
                        || record.purchaseOrderNumber
                        || record.statusMessage
                        || '';

                    return (
                        <FlexBox justifyStart wrap gap={0}>
                            <Text strong>
                                {record.endpoint}&nbsp;
                            </Text>
                            {summary && (
                                <Text type="secondary" ellipsis style={{ maxWidth: '100%' }}>
                                    {summary}&nbsp;
                                </Text>
                            )}
                            <Tag color={outcomeTag.color} style={{ margin: 0 }}>
                                {outcomeTag.label}
                            </Tag>
                            {labels}
                            {timestamp && (
                                <Text type="secondary">
                                    {timestamp}
                                </Text>
                            )}
                        </FlexBox>
                    );
                }

                if (record.type === 'attachment') {
                    const summary = `${record.previousFileName || '(unnamed)'} → ${record.nextFileName || '(unnamed)'}`;
                    return (
                        <FlexBox justifyStart wrap gap={0}>
                            <AttachmentFileTypeIcon fileName={record.nextFileName} />
                            <Text strong>
                                &nbsp;Renamed:&nbsp;
                            </Text>
                            <Text type="secondary" ellipsis style={{ maxWidth: '100%' }}>
                                {summary}&nbsp;
                            </Text>
                            {labels}
                            {timestamp && (
                                <Text type="secondary">
                                    {timestamp}
                                </Text>
                            )}
                        </FlexBox>
                    );
                }

                const from = record.from || '—';
                const subject = record.subject || '(No subject)';
                const storedCount = record.attachments
                    ? Object.keys(record.attachments).length
                    : 0;
                const outboundCount = record.outboundAttachmentLabels
                    ? record.outboundAttachmentLabels.length
                    : 0;
                const attachmentCount = Math.min(storedCount > 0 ? storedCount : outboundCount, 99);
                const hasAttachments = record.hasAttachments;

                return (
                    <FlexBox justifyStart wrap gap={0}>
                        <Text ellipsis>
                            {from}&nbsp;
                        </Text>
                        <Text type="secondary" ellipsis>
                            {subject}&nbsp;
                        </Text>
                        {renderTag(record.tag)}&nbsp;
                        {hasAttachments && (
                            <Tag
                                variant="outlined"
                                style={{ borderRadius: 30, margin: 0 }}
                            >
                                <PaperClipOutlined /> {attachmentCount}
                            </Tag>
                        )}
                        {labels}
                        {timestamp && (
                            <Text type="secondary">
                                {timestamp}
                            </Text>
                        )}
                    </FlexBox>
                );
            },
    });

    if (!pdfExportMode) {
        baseColumns.push({
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 150,
            fixed: fixedActions ? 'right' : undefined,
            className: 'no-print',
            render: (_: any, record: CommunicationRecord) => (
                <RenderActions
                    record={record}
                    disabled={disabled}
                    onDelete={onDelete}
                    onEmailSave={onEmailSave}
                    onPhoneSave={onPhoneSave}
                    onLabelsSave={onLabelsSave}
                    onCheckOrderStatus={onCheckOrderStatus}
                    labelSet={labelSet}
                />
            ),
        });
    }

    return baseColumns;
};
